/**
 * Side-panel for anchored review.
 *
 * Drives the comment + suggestion lifecycle:
 *
 * - Add a comment anchored to a field or to the currently-visible page.
 * - Reply to a thread, edit the root comment body, resolve / reopen,
 *   delete.
 * - Create a field-value suggestion, accept / reject / withdraw.
 * - Click an item to jump the viewer to its anchor's page (the
 *   `ReviewFocusProvider` carries the request to the parent, which calls
 *   the viewer's imperative scroll-to-page handle).
 *
 * Capability flags from the backend (`can_edit`, `can_resolve`, etc.)
 * decide which controls are rendered — keep the server-side ACLs as the
 * source of truth so a future role change doesn't require updating two
 * places.
 */
import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import {
  Check,
  CornerDownRight,
  Loader2,
  MessageSquare,
  MoreHorizontal,
  Pencil,
  RotateCcw,
  Trash2,
  X,
} from 'lucide-react'

import {
  useCreateComment,
  useCreateSuggestion,
  useDeleteComment,
  useDocumentComments,
  useDocumentSuggestions,
  useResolveSuggestion,
  useUpdateComment,
} from '@/api/queries'
import type {
  DocumentDetail,
  DocumentSuggestion,
  Field,
} from '@/api/docfiller-types'
import { fieldDisplayName } from '@/api/docfiller-types'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Textarea, Input } from '@/components/ui/input'
import { EmptyState } from '@/components/ui/empty-state'
import { cn } from '@/lib/utils'

import { groupReview, type CommentThread } from './types'
import { useReviewFocus } from './useReviewFocus'

interface ReviewPanelProps {
  doc: DocumentDetail
  /** 1-based page index of the page currently centered in the viewer. */
  currentPage: number
}

export function ReviewPanel({ doc, currentPage }: ReviewPanelProps) {
  const docUuid = doc.uuid
  const commentsQ = useDocumentComments(docUuid)
  const suggestionsQ = useDocumentSuggestions(docUuid)

  const grouped = useMemo(
    () => groupReview(commentsQ.data ?? [], suggestionsQ.data ?? []),
    [commentsQ.data, suggestionsQ.data],
  )

  const focus = useReviewFocus()

  if (commentsQ.isLoading || suggestionsQ.isLoading) {
    return (
      <div
        className="flex h-32 items-center justify-center text-sm text-muted-foreground"
        data-testid="review-panel-loading"
      >
        <Loader2 className="size-5 animate-spin" aria-hidden />
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col gap-4" data-testid="review-panel">
      <NewCommentForm
        docUuid={docUuid}
        currentPage={currentPage}
        fields={doc.fields}
      />
      <NewSuggestionForm docUuid={docUuid} fields={doc.fields} />

      <section data-testid="review-comments-list" className="space-y-2">
        <header className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">
            Comments{' '}
            <span className="text-xs text-muted-foreground">
              ({grouped.threads.length})
            </span>
          </h3>
        </header>
        {grouped.threads.length === 0 ? (
          <EmptyState
            icon={MessageSquare}
            title="No comments yet"
            description="Anchor a comment to a field or page region to start a review thread."
          />
        ) : (
          <ul className="space-y-2">
            {grouped.threads.map((thread) => (
              <li key={thread.root.uuid}>
                <CommentThreadView
                  thread={thread}
                  docUuid={docUuid}
                  fields={doc.fields}
                  focusedUuid={focus.focused?.uuid ?? null}
                  onFocus={() =>
                    focus.focus({
                      kind: 'comment',
                      uuid: thread.root.uuid,
                      page: thread.root.anchor.page,
                    })
                  }
                />
              </li>
            ))}
          </ul>
        )}
      </section>

      <section data-testid="review-suggestions-list" className="space-y-2">
        <header className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">
            Suggestions{' '}
            <span className="text-xs text-muted-foreground">
              ({grouped.allSuggestions.length})
            </span>
          </h3>
        </header>
        {grouped.allSuggestions.length === 0 ? (
          <EmptyState
            icon={MoreHorizontal}
            title="No suggestions yet"
            description="Propose a value change on any field and reviewers can accept or reject it."
          />
        ) : (
          <ul className="space-y-2">
            {grouped.allSuggestions.map((s) => (
              <li key={s.uuid}>
                <SuggestionRow
                  suggestion={s}
                  docUuid={docUuid}
                  focusedUuid={focus.focused?.uuid ?? null}
                  onFocus={() =>
                    focus.focus({
                      kind: 'suggestion',
                      uuid: s.uuid,
                      page: s.anchor?.page ?? 1,
                    })
                  }
                />
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}

// ─────────────────────────── New comment form ─────────────────────────────

function NewCommentForm({
  docUuid,
  currentPage,
  fields,
}: {
  docUuid: string
  currentPage: number
  fields: Field[]
}) {
  const [body, setBody] = useState('')
  const [fieldUuid, setFieldUuid] = useState<string>('')
  const create = useCreateComment(docUuid)

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const text = body.trim()
    if (!text) {
      toast.error('Comment must not be empty')
      return
    }
    try {
      const useField = fieldUuid && fields.find((f) => f.uuid === fieldUuid)
      await create.mutateAsync({
        body: text,
        anchor: useField
          ? {
              type: 'field',
              page: pageForField(useField, currentPage),
              field_uuid: useField.uuid,
            }
          : {
              type: 'region',
              page: currentPage,
              rect: { x: 16, y: 16, width: 200, height: 64 },
            },
      })
      setBody('')
      setFieldUuid('')
      toast.success('Comment added')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to add comment')
    }
  }

  return (
    <form
      className="min-w-0 space-y-3 rounded-md border border-border bg-card/40 p-3"
      onSubmit={onSubmit}
      data-testid="review-new-comment"
    >
      <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Add a comment
      </h4>
      <Textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={2}
        placeholder="Leave a comment for the team…"
        aria-label="Comment body"
        data-testid="review-new-comment-body"
      />
      <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-end">
        <label className="flex min-w-0 flex-1 flex-col gap-1 text-xs text-muted-foreground sm:flex-row sm:items-center">
          <span className="shrink-0">Anchor:</span>
          <select
            className="h-10 w-full min-w-0 max-w-full rounded-sm border border-input bg-card px-2 text-sm sm:h-8 sm:text-xs"
            value={fieldUuid}
            onChange={(e) => setFieldUuid(e.target.value)}
            data-testid="review-new-comment-anchor"
            aria-label="Comment anchor"
          >
            <option value="">Current page (page {currentPage})</option>
            {fields.map((f) => (
              <option key={f.uuid} value={f.uuid}>
                Field — {fieldDisplayName(f)}
              </option>
            ))}
          </select>
        </label>
        <Button
          type="submit"
          size="sm"
          disabled={create.isPending || body.trim().length === 0}
          data-testid="review-new-comment-submit"
          className="min-h-10 self-start sm:min-h-8"
        >
          {create.isPending ? <Loader2 className="animate-spin" /> : 'Add'}
        </Button>
      </div>
    </form>
  )
}

function pageForField(field: Field, fallback: number): number {
  const anchor = field.anchor as Record<string, unknown> | null | undefined
  const p = anchor && typeof anchor.page === 'number' ? anchor.page : fallback
  return Math.max(1, Math.floor(p))
}

// ─────────────────────────── New suggestion form ──────────────────────────

function NewSuggestionForm({
  docUuid,
  fields,
}: {
  docUuid: string
  fields: Field[]
}) {
  const [fieldUuid, setFieldUuid] = useState<string>(fields[0]?.uuid ?? '')
  const [proposed, setProposed] = useState('')
  const [note, setNote] = useState('')
  const create = useCreateSuggestion(docUuid)

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!fieldUuid) {
      toast.error('Pick a field first')
      return
    }
    try {
      const field = fields.find((f) => f.uuid === fieldUuid)
      const page = field ? pageForField(field, 1) : 1
      await create.mutateAsync({
        field_uuid: fieldUuid,
        proposed_value: proposed.length === 0 ? null : proposed,
        note: note.trim().length === 0 ? null : note.trim(),
        anchor: { type: 'field', page, field_uuid: fieldUuid },
      })
      setProposed('')
      setNote('')
      toast.success('Suggestion submitted')
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to submit suggestion',
      )
    }
  }

  if (fields.length === 0) return null

  return (
    <form
      className="min-w-0 space-y-3 rounded-md border border-border bg-card/40 p-3"
      onSubmit={onSubmit}
      data-testid="review-new-suggestion"
    >
      <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Propose a field change
      </h4>
      <label className="flex flex-col gap-1 text-xs text-muted-foreground">
        Field
        <select
          className="h-10 w-full min-w-0 max-w-full rounded-sm border border-input bg-card px-2 text-sm sm:h-8 sm:text-xs"
          value={fieldUuid}
          onChange={(e) => setFieldUuid(e.target.value)}
          data-testid="review-new-suggestion-field"
          aria-label="Field to propose change for"
        >
          {fields.map((f) => (
            <option key={f.uuid} value={f.uuid}>
              {fieldDisplayName(f)}
              {f.current_value ? ` — currently ${f.current_value}` : ''}
            </option>
          ))}
        </select>
      </label>
      <Input
        value={proposed}
        onChange={(e) => setProposed(e.target.value)}
        placeholder="Proposed value (leave empty to clear)"
        aria-label="Proposed value"
        data-testid="review-new-suggestion-value"
      />
      <Input
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Note (optional)"
        aria-label="Suggestion note"
        data-testid="review-new-suggestion-note"
      />
      <Button
        type="submit"
        size="sm"
        disabled={create.isPending || !fieldUuid}
        data-testid="review-new-suggestion-submit"
        className="min-h-10 self-start sm:min-h-8"
      >
        {create.isPending ? <Loader2 className="animate-spin" /> : 'Propose'}
      </Button>
    </form>
  )
}

// ─────────────────────────── Comment thread view ──────────────────────────

function CommentThreadView({
  thread,
  docUuid,
  fields,
  focusedUuid,
  onFocus,
}: {
  thread: CommentThread
  docUuid: string
  fields: Field[]
  focusedUuid: string | null
  onFocus: () => void
}) {
  const { root, replies } = thread
  const update = useUpdateComment(docUuid)
  const del = useDeleteComment(docUuid)
  const create = useCreateComment(docUuid)
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(root.body)
  const [replyDraft, setReplyDraft] = useState('')

  const anchorLabel = useMemo(() => {
    if (root.anchor.type === 'field' && root.anchor.field_uuid) {
      const f = fields.find((field) => field.uuid === root.anchor.field_uuid)
      return f
        ? `Field — ${fieldDisplayName(f)}`
        : `Field on page ${root.anchor.page}`
    }
    return `Page ${root.anchor.page}`
  }, [root.anchor, fields])

  const isFocused = focusedUuid === root.uuid
  return (
    <article
      className={cn(
        'rounded-md border border-border bg-background p-3 transition-colors',
        root.is_resolved && 'opacity-70',
        isFocused && 'ring-2 ring-primary/40',
      )}
      data-testid="review-thread"
      data-comment-uuid={root.uuid}
    >
      <button
        type="button"
        onClick={onFocus}
        className="flex w-full items-start justify-between gap-2 text-left"
        aria-label={`Jump to comment by ${root.author_name} on page ${root.anchor.page}`}
        data-testid="review-thread-jump"
      >
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span className="truncate font-medium text-foreground">
              {root.author_name || root.author_email}
            </span>
            <Badge tone="outline" size="xs">
              {anchorLabel}
            </Badge>
            {root.is_resolved && (
              <Badge tone="success" size="xs">
                Resolved
              </Badge>
            )}
          </div>
          {editing ? (
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={2}
              className="mt-1"
              aria-label="Edit comment body"
              data-testid="review-thread-edit-body"
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <p
              className="mt-1 whitespace-pre-wrap text-sm"
              data-testid="review-thread-body"
            >
              {root.body}
            </p>
          )}
          {root.edited_at && !editing && (
            <p className="mt-1 text-[10px] text-muted-foreground">edited</p>
          )}
        </div>
      </button>

      <div
        className="mt-2 flex flex-wrap items-center gap-1"
        onClick={(e) => e.stopPropagation()}
      >
        {root.can_edit && !editing && (
          <Button
            type="button"
            size="icon-sm"
            variant="ghost"
            aria-label="Edit comment"
            data-testid="review-thread-edit"
            onClick={() => setEditing(true)}
          >
            <Pencil />
          </Button>
        )}
        {editing && (
          <>
            <Button
              type="button"
              size="sm"
              variant="default"
              data-testid="review-thread-save"
              onClick={async () => {
                const text = draft.trim()
                if (!text) {
                  toast.error('Comment must not be empty')
                  return
                }
                try {
                  await update.mutateAsync({
                    commentUuid: root.uuid,
                    body: { body: text },
                  })
                  setEditing(false)
                } catch (err) {
                  toast.error(
                    err instanceof Error ? err.message : 'Save failed',
                  )
                }
              }}
              disabled={update.isPending}
            >
              {update.isPending ? <Loader2 className="animate-spin" /> : 'Save'}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              data-testid="review-thread-cancel-edit"
              onClick={() => {
                setEditing(false)
                setDraft(root.body)
              }}
            >
              Cancel
            </Button>
          </>
        )}
        {root.can_resolve &&
          (root.is_resolved ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              data-testid="review-thread-reopen"
              onClick={async () => {
                try {
                  await update.mutateAsync({
                    commentUuid: root.uuid,
                    body: { resolved: false },
                  })
                } catch (err) {
                  toast.error(
                    err instanceof Error ? err.message : 'Reopen failed',
                  )
                }
              }}
            >
              <RotateCcw />
              Reopen
            </Button>
          ) : (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              data-testid="review-thread-resolve"
              onClick={async () => {
                try {
                  await update.mutateAsync({
                    commentUuid: root.uuid,
                    body: { resolved: true },
                  })
                } catch (err) {
                  toast.error(
                    err instanceof Error ? err.message : 'Resolve failed',
                  )
                }
              }}
            >
              <Check />
              Resolve
            </Button>
          ))}
        {root.can_delete && !editing && (
          <Button
            type="button"
            size="icon-sm"
            variant="ghost"
            aria-label="Delete comment"
            data-testid="review-thread-delete"
            onClick={async () => {
              if (typeof window !== 'undefined') {
                const ok = window.confirm(
                  'Delete this comment? This cannot be undone.',
                )
                if (!ok) return
              }
              try {
                await del.mutateAsync(root.uuid)
              } catch (err) {
                toast.error(err instanceof Error ? err.message : 'Delete failed')
              }
            }}
          >
            <Trash2 />
          </Button>
        )}
      </div>

      {replies.length > 0 && (
        <ul
          className="mt-3 space-y-2 border-l-2 border-border pl-3"
          data-testid="review-thread-replies"
        >
          {replies.map((r) => (
            <li key={r.uuid}>
              <div className="text-xs text-muted-foreground">
                <span className="font-medium text-foreground">
                  {r.author_name || r.author_email}
                </span>
              </div>
              <p className="whitespace-pre-wrap text-sm">{r.body}</p>
            </li>
          ))}
        </ul>
      )}

      {!root.is_resolved && (
        <form
          className="mt-3 flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center"
          onSubmit={async (e) => {
            e.preventDefault()
            const text = replyDraft.trim()
            if (!text) return
            try {
              await create.mutateAsync({
                body: text,
                anchor: root.anchor,
                parent_comment_uuid: root.uuid,
              })
              setReplyDraft('')
            } catch (err) {
              toast.error(err instanceof Error ? err.message : 'Reply failed')
            }
          }}
        >
          <CornerDownRight className="size-3.5 text-muted-foreground" />
          <Input
            value={replyDraft}
            onChange={(e) => setReplyDraft(e.target.value)}
            placeholder="Reply…"
            aria-label="Reply body"
            data-testid="review-thread-reply-body"
          />
          <Button
            type="submit"
            size="sm"
            variant="outline"
            disabled={create.isPending || replyDraft.trim().length === 0}
            data-testid="review-thread-reply-submit"
            className="min-h-10 self-start sm:min-h-8"
          >
            Reply
          </Button>
        </form>
      )}
    </article>
  )
}

// ─────────────────────────── Suggestion row ───────────────────────────────

function SuggestionRow({
  suggestion,
  docUuid,
  focusedUuid,
  onFocus,
}: {
  suggestion: DocumentSuggestion
  docUuid: string
  focusedUuid: string | null
  onFocus: () => void
}) {
  const resolve = useResolveSuggestion(docUuid)
  const isFocused = focusedUuid === suggestion.uuid
  const label =
    suggestion.field_friendly_name ??
    suggestion.field_name ??
    'Field-value change'

  const statusTone =
    suggestion.status === 'accepted'
      ? ('success' as const)
      : suggestion.status === 'rejected' || suggestion.status === 'withdrawn'
        ? ('outline' as const)
        : ('info' as const)

  return (
    <article
      className={cn(
        'rounded-md border border-border bg-background p-3 transition-colors',
        suggestion.status !== 'pending' && 'opacity-80',
        isFocused && 'ring-2 ring-primary/40',
      )}
      data-testid="review-suggestion"
      data-suggestion-uuid={suggestion.uuid}
    >
      <button
        type="button"
        onClick={onFocus}
        className="flex w-full items-start justify-between gap-2 text-left"
        aria-label={`Jump to suggestion on ${label}`}
        data-testid="review-suggestion-jump"
      >
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span className="truncate font-medium text-foreground">{label}</span>
            <Badge tone={statusTone} size="xs">
              {suggestion.status}
            </Badge>
          </div>
          <p className="mt-1 text-sm" data-testid="review-suggestion-body">
            <span className="text-muted-foreground">from </span>
            <code className="rounded bg-muted px-1 py-0.5 text-xs">
              {suggestion.original_value ?? '(empty)'}
            </code>
            <span className="text-muted-foreground"> → </span>
            <code className="rounded bg-muted px-1 py-0.5 text-xs">
              {suggestion.proposed_value ?? '(empty)'}
            </code>
          </p>
          {suggestion.note && (
            <p className="mt-1 text-xs text-muted-foreground">
              {suggestion.note}
            </p>
          )}
          <p className="mt-1 text-[10px] text-muted-foreground">
            by {suggestion.author_name || suggestion.author_email}
          </p>
        </div>
      </button>

      {suggestion.status === 'pending' && (
        <div
          className="mt-2 flex flex-wrap items-center gap-1"
          onClick={(e) => e.stopPropagation()}
        >
          {suggestion.can_accept && (
            <Button
              type="button"
              size="sm"
              variant="default"
              disabled={resolve.isPending}
              data-testid="review-suggestion-accept"
              onClick={async () => {
                try {
                  await resolve.mutateAsync({
                    suggestionUuid: suggestion.uuid,
                    action: 'accept',
                  })
                  toast.success('Applied suggestion')
                } catch (err) {
                  toast.error(
                    err instanceof Error ? err.message : 'Accept failed',
                  )
                }
              }}
            >
              <Check />
              Accept
            </Button>
          )}
          {suggestion.can_reject && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={resolve.isPending}
              data-testid="review-suggestion-reject"
              onClick={async () => {
                try {
                  await resolve.mutateAsync({
                    suggestionUuid: suggestion.uuid,
                    action: 'reject',
                  })
                } catch (err) {
                  toast.error(
                    err instanceof Error ? err.message : 'Reject failed',
                  )
                }
              }}
            >
              <X />
              Reject
            </Button>
          )}
          {suggestion.can_withdraw && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={resolve.isPending}
              data-testid="review-suggestion-withdraw"
              onClick={async () => {
                try {
                  await resolve.mutateAsync({
                    suggestionUuid: suggestion.uuid,
                    action: 'withdraw',
                  })
                } catch (err) {
                  toast.error(
                    err instanceof Error ? err.message : 'Withdraw failed',
                  )
                }
              }}
            >
              Withdraw
            </Button>
          )}
        </div>
      )}
    </article>
  )
}
